/**
 *
 *  useContentScrollerControls
 *  Hook
 *  An object that enables body scroll locking (for mobile and desktop Safari/Chrome/Firefox)
 *  without breaking scrolling of a target element (eg. modal/lightbox/flyouts/nav-menus).
 *
 *  Created by Vladimir Shishlyannikov on 01/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import { useCallback } from "react";
import useSyncRef from "../useSyncRef/useSyncRef";
// import useSmoothScrollPolyfill from "../useSmoothScrollPolyfill/useSmoothScrollPolyfill";
import getScrollOffset from "../../utils/getScrollOffset";
import matchMedia from "../../utils/matchMedia";

export const getScrollTargetFunc = ({
  direction,
  totalCount,
  loop,
  visibleItems,
}) => {
  if (visibleItems == null || totalCount == null) {
    return 0;
  }

  if (visibleItems.length === 0) {
    return direction === "forward" ? 1 : totalCount - 1;
  }

  const s =
    direction === "forward"
      ? Math.max(...visibleItems) + 1
      : Math.min(...visibleItems) - 1;

  return loop
    ? direction === "forward"
      ? s % totalCount
      : s < 0
      ? totalCount - 1
      : s
    : direction === "forward"
    ? Math.min(s, totalCount - 1)
    : Math.max(s, 0);
};

const getScrollAlignment = (unit, direction) =>
  unit === "slide" ? "nearest" : direction === "forward" ? "start" : "end";

export default function useContentScrollerControls({
  contentScrollerApi,
  loop = false, // true,
  unit = "page",
  getScrollTarget = getScrollTargetFunc,
}) {
  const getScrollTargetRef = useSyncRef(getScrollTarget),
    _direction = "ltr";

  const handleScroll = useCallback(
    ({
      behavior,
      block = "nearest",
      direction: scrollDirection,
      inline = scrollDirection === "forward" ? "start" : "end",
      nextElementKey,
      resolve,
      shouldUseScrollIntoView = true,
      spaceBetweenItems,
    }) => {
      var state;

      if (contentScrollerApi == null) {
        return;
      }

      state = contentScrollerApi.state;

      const lastKnownState =
          contentScrollerApi == null || state === null || state === undefined
            ? undefined
            : state.current,
        targetElement =
          lastKnownState == null
            ? undefined
            : lastKnownState.childElementByKey.get(nextElementKey),
        _behavior = behavior != null ? behavior : "smooth",
        options = {
          block,
          behavior: matchMedia() ? "auto" : _behavior,
          inline,
        };

      window.requestAnimationFrame(() => {
        if (
          resolve == null ||
          resolve(targetElement) ||
          shouldUseScrollIntoView
        ) {
          if (targetElement != null) {
            targetElement.scrollIntoView(options);
          }
        } else if (
          contentScrollerApi != null &&
          contentScrollerApi.scrollingContainer &&
          targetElement
        ) {
          const left = getScrollOffset(
            contentScrollerApi == null
              ? undefined
              : contentScrollerApi.scrollingContainer,
            targetElement,
            _direction,
            spaceBetweenItems
          );

          if (contentScrollerApi != null) {
            contentScrollerApi.scrollingContainer.scrollTo({
              left: left - 8,
              ...options,
            });
          }

          if (contentScrollerApi != null) {
            contentScrollerApi.recalc({
              reason: "child_visible_changed",
              key: nextElementKey,
            });
          }
        }

        if (resolve != null) {
          resolve(targetElement);
        }
      });
    },
    [contentScrollerApi, _direction]
  );

  const scrollTo = useCallback(
      ({
        behavior,
        direction: _direction,
        unit: _unit,
        nextElementKey,
        shouldUseScrollIntoView,
        spaceBetweenItems,
      }) => {
        var state = contentScrollerApi.state;

        const lastKnownState =
            contentScrollerApi === null || state === undefined
              ? undefined
              : state.current,
          totalCount =
            contentScrollerApi == null
              ? undefined
              : contentScrollerApi.getTotalCount();

        if (!lastKnownState || totalCount == null) {
          return;
        }

        const visibleItemIndexes = contentScrollerApi.getVisibleItemIndexes(),
          _nextElementKey =
            nextElementKey != null
              ? nextElementKey
              : getScrollTargetRef.current({
                  direction: _direction,
                  loop,
                  totalCount,
                  visibleItems: visibleItemIndexes,
                }),
          inline = getScrollAlignment(_unit || unit, _direction);

        return new Promise((res) => {
          window.requestAnimationFrame(() => {
            handleScroll({
              behavior,
              direction: _direction,
              nextElementKey: _nextElementKey,
              resolve: res,
              inline,
              shouldUseScrollIntoView,
              spaceBetweenItems,
            });

            contentScrollerApi.onOperation({
              reason:
                "forward" === _direction ? "page_forward" : "page_reverse",
            });
          });
        });
      },
      [contentScrollerApi, loop, getScrollTargetRef, handleScroll, unit]
    ),
    handleReverse = useCallback(() => {
      scrollTo({
        direction: "reverse",
        spaceBetweenItems: 0,
        shouldUseScrollIntoView: false,
      });
    }, [scrollTo]);

  return {
    onPressForward: useCallback(() => {
      scrollTo({
        direction: "forward",
        spaceBetweenItems: 0,
        shouldUseScrollIntoView: false,
      });
    }, [scrollTo]),
    onPressReverse: handleReverse,
    scrollTo: scrollTo,
  };
}
